import React, { useState } from 'react';
import {
  Alert, Box, Button, ButtonBase, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, InputAdornment,
  Link, Stack, TextField, Typography, alpha, useTheme,
} from '@mui/material';
import { LockOutlined, Visibility, VisibilityOff } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { emailService } from '../../services/emailService';
import type { EmailSettings, MailProvider, MailProviderInfo, OwnMailbox } from '../../types/email';

interface Props {
  providers: MailProviderInfo[];
  /** The mailbox connected now, when changing or reconnecting it. */
  current: OwnMailbox | null;
  onClose: () => void;
  onConnected: (next: EmailSettings) => void;
}

/** What each provider needs, in the school admin's words. */
const HELP: Record<MailProvider, React.ReactNode> = {
  gmail: (
    <>
      Use an <b>app password</b>, not your normal Gmail password. Turn on 2-Step Verification, then create one at{' '}
      <Link href="https://myaccount.google.com/apppasswords" target="_blank" rel="noopener">myaccount.google.com/apppasswords</Link>.
    </>
  ),
  outlook: 'Your Microsoft 365 administrator may need to allow “Authenticated SMTP” for this mailbox first.',
  zoho: 'If two-factor sign-in is on for this mailbox, use an app-specific password from Zoho.',
  other: 'Your email host lists these under “SMTP” or “outgoing mail” settings.',
};

/** Connect the school's own mailbox: it signs in and sends itself a test before anything is saved. */
const ConnectMailboxDialog: React.FC<Props> = ({ providers, current, onClose, onConnected }) => {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const [provider, setProvider] = useState<MailProvider>(current?.provider ?? 'gmail');
  const [username, setUsername] = useState(current?.username ?? '');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [host, setHost] = useState(current?.provider === 'other' ? current.host : '');
  const [port, setPort] = useState(String(current?.provider === 'other' ? current.port : 587));
  const [saving, setSaving] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  const other = provider === 'other';
  const ready = username.trim() && password && (!other || (host.trim() && port));

  const connect = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!ready || saving) return;
    setSaving(true);
    setProblem(null);
    try {
      const next = await emailService.connectMailbox({
        provider, username: username.trim(), password, ...(other ? { host: host.trim(), port: Number(port) } : {}),
      });
      enqueueSnackbar(`Connected. A test email was sent to ${username.trim()}.`, { variant: 'success' });
      onConnected(next);
    } catch (err: any) {
      setProblem(err?.message || 'Couldn’t connect the mailbox');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onClose={() => !saving && onClose()} maxWidth="sm" fullWidth>
      <form onSubmit={connect} noValidate>
        <DialogTitle component="div" sx={{ pb: 1 }}>
          <Typography variant="h6" fontWeight={700}>{current ? 'Change mailbox' : 'Send from your own email'}</Typography>
          <Typography variant="caption" color="text.secondary">
            Families will see emails come from this address, and their replies reach it.
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2.5}>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(4, 1fr)' }, gap: 1 }}>
              {providers.map(p => {
                const active = p.key === provider;
                return (
                  <ButtonBase
                    key={p.key}
                    onClick={() => { setProvider(p.key); setProblem(null); }}
                    aria-pressed={active}
                    sx={{
                      borderRadius: '10px', px: 1.5, py: 1.25, textAlign: 'left', justifyContent: 'flex-start',
                      border: 1, borderColor: active ? 'primary.main' : 'divider',
                      bgcolor: active ? alpha(theme.palette.primary.main, 0.06) : 'transparent',
                      boxShadow: active ? `0 0 0 1px ${theme.palette.primary.main}` : 'none',
                      transition: theme.transitions.create(['border-color', 'background-color'], { duration: 150 }),
                      '&:hover': { borderColor: active ? 'primary.main' : 'text.disabled' },
                    }}
                  >
                    <Typography variant="body2" fontWeight={active ? 700 : 500} color={active ? 'primary.main' : 'text.primary'} sx={{ lineHeight: 1.3 }}>
                      {p.label}
                    </Typography>
                  </ButtonBase>
                );
              })}
            </Box>

            <TextField
              label="Email address" type="email" value={username} onChange={e => setUsername(e.target.value)}
              placeholder="admissions@yourschool.edu.pk" fullWidth autoComplete="off"
            />
            <TextField
              label={provider === 'gmail' ? 'App password' : 'Password'} value={password} onChange={e => setPassword(e.target.value)}
              type={showPassword ? 'text' : 'password'} fullWidth autoComplete="new-password"
              placeholder={current ? 'Enter it again to reconnect' : undefined}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setShowPassword(v => !v)} aria-label={showPassword ? 'Hide password' : 'Show password'}>
                      {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            {other && (
              <Stack direction="row" spacing={2}>
                <TextField label="Mail server" value={host} onChange={e => setHost(e.target.value)} placeholder="mail.yourschool.edu.pk" sx={{ flex: 1 }} />
                <TextField label="Port" value={port} onChange={e => setPort(e.target.value.replace(/\D/g, ''))} inputMode="numeric" sx={{ width: 110 }} helperText="465 or 587" />
              </Stack>
            )}
            <Typography variant="body2" color="text.secondary">{HELP[provider]}</Typography>

            {problem && <Alert severity="error" sx={{ borderRadius: '10px' }}>{problem}</Alert>}

            <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ color: 'text.secondary' }}>
              <LockOutlined sx={{ fontSize: 16, mt: '2px' }} />
              <Typography variant="caption">
                We sign in and send a test email to this mailbox before saving it. The password is stored encrypted and never shown again.
              </Typography>
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={onClose} color="inherit" disabled={saving}>Cancel</Button>
          <Button type="submit" variant="contained" disableElevation disabled={!ready || saving}>
            {saving ? 'Testing…' : 'Test and connect'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default ConnectMailboxDialog;
